import type {
  SpeakerTurn,
  EditorReadiness,
  ExportSettings,
  ExportState,
  EditorAiStatus,
  FocusPoint,
  EditReply,
  EditRequest,
  MusicChoice,
  MusicTrack,
  ProjectSound,
  ProjectSoundState,
  AttachmentUpload,
  ProjectAsset,
  ProjectMediaLink,
  ProjectOp,
  ProjectState,
  ReactionEmoji,
  TranscriptWord,
  VideoAnalytics,
  VideoChapter,
  VideoEditorMaterial,
  VideoFeedback,
  VideoFrames,
  VideoLinks,
  VideoSegment,
  VideoAccess,
  VideoSummary,
  VideoTranscript,
} from '@live-stream/shared'
import { VIDEO_UPLOAD_MAX_BYTES, videoUploadType } from '@live-stream/shared'
import type { HttpClient } from '../http.js'
import { readKey, writeKey, type KeyValueStore } from '../storage.js'
import type { ReadableStore } from '../store.js'
import { putWithProgress } from '../meeting/attachments.js'
import { VideoPage } from '../videos/page.js'
import { ProjectEditor } from '../videos/project-editor.js'
import { viewerId } from '../videos/watch.js'

/** What an owner may change about a video; anything left out stays as it is. */
export interface VideoUpdate {
  title?: string
  description?: string
  /** `participants` only for a meeting's recording: whoever was in the meeting. */
  access?: VideoAccess
  /** A new password, or null to remove it. */
  password?: string | null
  /** ms since the epoch, or null for never. */
  expiresAt?: number | null
  commentsEnabled?: boolean
  /** On the original's clock, as the editor keeps them. */
  chapters?: VideoChapter[]
}

/** A video as its share link shows it. */
export interface SharedVideoView {
  video: VideoSummary
  links: VideoLinks
  /** The viewer is the owner: nothing they watch is counted. */
  mine: boolean
}

/** Who speaks, after a change by hand: the words with their people, and the turns. */
export interface SpeakersNow {
  words: TranscriptWord[]
  turns: SpeakerTurn[]
}

/** The signed-in person's videos, and each one's owner-side actions. */
export class Videos {
  constructor(
    private readonly http: HttpClient,
    private readonly local: KeyValueStore | null,
    private readonly signedIn: ReadableStore<boolean>,
  ) {}

  /**
   * The person's videos, newest first, with a poster link for each that has
   * one — and the recordings of meetings they were in that the host shared
   * with the participants (their ids in `shared`).
   */
  list(): Promise<{
    videos: VideoSummary[]
    posters: Record<string, string | null>
    shared: string[]
  }> {
    return this.http.get('/videos')
  }

  /** A new video, ready to take parts: the capture engine's first call. */
  create(input: { mimeType: string; title?: string }): Promise<{
    video: VideoSummary
    partBytes: number
  }> {
    return this.http.post('/videos', input)
  }

  /** Where to put one part of the upload. */
  partAddress(videoId: string, partNumber: number): Promise<AttachmentUpload> {
    return this.http.post(`/videos/${videoId}/parts/${partNumber}`)
  }

  /** Every part is up; the server assembles and processes it. */
  async complete(
    videoId: string,
    info: { durationMs: number | null; width?: number; height?: number },
  ): Promise<void> {
    await this.http.post(`/videos/${videoId}/complete`, info)
  }

  /** Give up on an upload; the half-made video is removed. */
  async abort(videoId: string): Promise<void> {
    await this.http.post(`/videos/${videoId}/abort`)
  }

  async update(videoId: string, change: VideoUpdate): Promise<VideoSummary> {
    return (await this.http.patch<{ video: VideoSummary }>(`/videos/${videoId}`, change)).video
  }

  async remove(videoId: string): Promise<void> {
    await this.http.delete(`/videos/${videoId}`)
  }

  /** A new share link; the old one stops working. */
  async newShareLink(videoId: string): Promise<VideoSummary> {
    return (await this.http.post<{ video: VideoSummary }>(`/videos/${videoId}/share-token`)).video
  }

  /** The video's edit as a project, for its owner's editor; made from the cut list the first time. */
  project(videoId: string): Promise<ProjectState> {
    return this.http.get(`/videos/${videoId}/project`)
  }

  /** Apply a batch of steps to revision `baseRev`: all or none. 409 `stale` when it moved on. */
  projectSteps(
    videoId: string,
    baseRev: number,
    ops: readonly ProjectOp[],
    label?: string,
  ): Promise<ProjectState> {
    return this.http.post(`/videos/${videoId}/project/steps`, { baseRev, ops, label })
  }

  projectUndo(videoId: string, baseRev: number): Promise<ProjectState> {
    return this.http.post(`/videos/${videoId}/project/undo`, { baseRev })
  }

  projectRedo(videoId: string, baseRev: number): Promise<ProjectState> {
    return this.http.post(`/videos/${videoId}/project/redo`, { baseRev })
  }

  /** Render the project into the MP4 people watch, in the background; the video says how far it has got. */
  async projectRender(videoId: string): Promise<VideoSummary> {
    return (await this.http.post<{ video: VideoSummary }>(`/videos/${videoId}/project/render`))
      .video
  }

  /**
   * "Save as a new video": the project rendered into a new video with its
   * own link; this one stays as it is. The new video is processing until done.
   */
  async projectCopy(videoId: string, title?: string): Promise<VideoSummary> {
    return (
      await this.http.post<{ video: VideoSummary }>(`/videos/${videoId}/project/copy`, { title })
    ).video
  }

  /**
   * Music or a picture from the owner's computer, for the project: put
   * straight into storage (with progress, 0–1), then looked at by the
   * server. Answers with the project file to add (an `addAsset` step);
   * refused when it is not music or a picture it can read.
   */
  async projectUpload(
    videoId: string,
    file: File,
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
  ): Promise<ProjectAsset> {
    const begun = await this.http.post<{
      key: string
      upload: { url: string; headers: Record<string, string> }
    }>(`/videos/${videoId}/project/uploads`, { name: file.name, type: file.type, size: file.size })
    await putWithProgress({
      url: begun.upload.url,
      headers: begun.upload.headers,
      body: file,
      onProgress: opts.onProgress,
      signal: opts.signal,
    })
    return (
      await this.http.post<{ asset: ProjectAsset }>(`/videos/${videoId}/project/uploads/done`, {
        key: begun.key,
        name: file.name,
      })
    ).asset
  }

  /** The owner's brand logo, copied into the project: the project file to add (an `addAsset` step). */
  async projectBrandLogo(videoId: string): Promise<ProjectAsset> {
    return (
      await this.http.post<{ asset: ProjectAsset }>(`/videos/${videoId}/project/brand-logo`, {})
    ).asset
  }

  /**
   * Make what the project's sound settings need, in the background (the
   * cleaned voice, how loud the recording is): where it stands. Ask again
   * until nothing is being made, then fetch the media again.
   */
  async projectSound(videoId: string, sound?: ProjectSound): Promise<ProjectSoundState> {
    return this.http.post<ProjectSoundState>(`/videos/${videoId}/project/sound`, { sound })
  }

  /**
   * Editing by request (E4): what the owner asks for, and Claude's proposed
   * changes (`requestOps` makes them steps), with what the request cost.
   */
  /**
   * Smart fill: where the action is in the main recording, as a path for
   * the crop to follow in a frame of this size (its padding taken off).
   * Saved in the layout (`fit: 'smart'`), the preview and the render follow it.
   */
  async projectSmartFrame(
    videoId: string,
    frame: { width: number; height: number; padding: number },
  ): Promise<{ path: FocusPoint[] }> {
    return this.http.post<{ path: FocusPoint[] }>(`/videos/${videoId}/project/smart-frame`, frame)
  }

  /** The AI editor's conversation about this edit, as the editor saved it. */
  async projectAssistantHistory(videoId: string): Promise<unknown[]> {
    return (
      await this.http.get<{ turns: unknown[] }>(`/videos/${videoId}/project/assistant/history`)
    ).turns
  }

  /** Keep the AI editor's conversation with the project. */
  async saveProjectAssistantHistory(videoId: string, turns: unknown[]): Promise<void> {
    await this.http.put(`/videos/${videoId}/project/assistant/history`, { turns })
  }

  /** The latest export of the edit: being made, ready (with its link), or failed; null when none. */
  async projectExport(videoId: string): Promise<ExportState | null> {
    return (
      await this.http.get<{ export: ExportState | null }>(`/videos/${videoId}/project/export`)
    ).export
  }

  /** Export the edit as a file in the format, size and quality chosen; made in the background. */
  async startProjectExport(videoId: string, settings: ExportSettings): Promise<ExportState> {
    return (
      await this.http.post<{ export: ExportState }>(`/videos/${videoId}/project/export`, settings)
    ).export
  }

  /** Whether the AI editor has a model, and which. */
  async projectAssistantStatus(videoId: string): Promise<EditorAiStatus> {
    return this.http.get<EditorAiStatus>(`/videos/${videoId}/project/assistant`)
  }

  async projectAssistant(videoId: string, request: EditRequest): Promise<EditReply> {
    return this.http.post<EditReply>(`/videos/${videoId}/project/assistant`, request)
  }

  /** Music free to put in videos, for a search: to listen to and choose from. */
  async projectMusicSearch(videoId: string, query: string): Promise<MusicChoice> {
    return this.http.get<MusicChoice>(
      `/videos/${videoId}/project/music?q=${encodeURIComponent(query)}`,
    )
  }

  /**
   * A chosen track, fetched into the project's files: the file to add (with
   * `musicBedOps`). The track as a search found it is fetched at once; one
   * named only by its id is looked up first.
   */
  async projectAddMusic(
    videoId: string,
    track: MusicTrack | string,
  ): Promise<{ asset: ProjectAsset; track: MusicTrack }> {
    return this.http.post<{ asset: ProjectAsset; track: MusicTrack }>(
      `/videos/${videoId}/project/music`,
      typeof track === 'string' ? { trackId: track } : { trackId: track.id, track },
    )
  }

  /** Where the editor plays each of the project's files from (small copies once they are made). */
  async projectMedia(videoId: string): Promise<ProjectMediaLink[]> {
    return (await this.http.get<{ media: ProjectMediaLink[] }>(`/videos/${videoId}/project/media`))
      .media
  }

  /** An editor for a video's project: changes show at once and are saved in order. */
  projectEditor(): ProjectEditor {
    return new ProjectEditor(this)
  }

  /** Frames to pick a thumbnail from. */
  frames(videoId: string): Promise<VideoFrames> {
    return this.http.get(`/videos/${videoId}/frames`)
  }

  /** The thumbnail from the frame at `atMs`, or the automatic one with null. */
  setPoster(
    videoId: string,
    atMs: number | null,
  ): Promise<{ video: VideoSummary; links: VideoLinks }> {
    return this.http.post(`/videos/${videoId}/poster`, {
      atMs: atMs === null ? null : Math.round(atMs),
    })
  }

  async analytics(videoId: string): Promise<VideoAnalytics> {
    return (await this.http.get<{ analytics: VideoAnalytics }>(`/videos/${videoId}/analytics`))
      .analytics
  }

  /** What the editor works on: the original's words, pauses and frames. */
  async editor(videoId: string): Promise<VideoEditorMaterial> {
    return (await this.http.get<{ material: VideoEditorMaterial }>(`/videos/${videoId}/editor`))
      .material
  }

  /**
   * Fix words the transcript got wrong: words `from`..`to` (as the editor
   * last saw them, `expect`) become `words`, in the same stretch of time
   * (make them with `respaceWords`). The recording is not touched. Answers
   * with every word; 409 `stale` when those words changed elsewhere.
   */
  async fixWords(
    videoId: string,
    fix: { from: number; to: number; expect: string[]; words: TranscriptWord[] },
  ): Promise<TranscriptWord[]> {
    return (
      await this.http.put<{ words: TranscriptWord[] }>(`/videos/${videoId}/transcript/words`, fix)
    ).words
  }

  /** Words from..to said by person `sp` (0 = Person 1): who speaks, put right by hand. */
  setSpeaker(videoId: string, from: number, to: number, sp: number): Promise<SpeakersNow> {
    return this.http.put<SpeakersNow>(`/videos/${videoId}/transcript/speakers`, { from, to, sp })
  }

  /** The stretch s..e of the recording said by person `sp`, or by no one (null). */
  setTurn(videoId: string, s: number, e: number, sp: number | null): Promise<SpeakersNow> {
    return this.http.put<SpeakersNow>(`/videos/${videoId}/transcript/turns`, { s, e, sp })
  }

  /** Person `from` is person `into`: one voice heard as two, joined. */
  mergeSpeakers(videoId: string, from: number, into: number): Promise<SpeakersNow> {
    return this.http.post<SpeakersNow>(`/videos/${videoId}/transcript/turns/merge`, { from, into })
  }

  /** What the editor waits for before it opens, step by step; each step not begun is started. */
  projectReadiness(videoId: string): Promise<EditorReadiness> {
    return this.http.get<EditorReadiness>(`/videos/${videoId}/project/readiness`)
  }

  /**
   * A video of the person's own, from a file (recorded anywhere): the video
   * is made at once, so it has its link and can be opened in the editor,
   * and `done` settles when every part is up and processing has begun.
   * Parts go up three at a time; `onProgress` hears the bytes sent.
   */
  async upload(
    file: Blob & { name?: string },
    opts: {
      title?: string
      onProgress?: (sent: number, total: number) => void
      signal?: AbortSignal
    } = {},
  ): Promise<{ video: VideoSummary; partBytes: number; done: Promise<void> }> {
    const mimeType = videoUploadType(file)
    if (!mimeType) {
      throw new Error('That file is not a video this app can open (MP4, MOV, WebM or MKV).')
    }
    if (file.size > VIDEO_UPLOAD_MAX_BYTES) throw new Error('That file is over 4 GB.')
    const title = opts.title ?? (file.name ?? '').replace(/\.[^.]+$/, '').slice(0, 140)
    const made = await this.create({ mimeType, title })
    const done = this.sendFile(made.video.id, file, made.partBytes, opts)
    // Settled by whoever waits on it; never an unhandled rejection meanwhile.
    done.catch(() => undefined)
    return { video: made.video, partBytes: made.partBytes, done }
  }

  /**
   * A file sent to a video that is taking parts (again, after an upload was
   * interrupted: the parts are sent anew), then finished.
   */
  async sendFile(
    videoId: string,
    file: Blob,
    partBytes: number = 5 * 1024 * 1024,
    opts: { onProgress?: (sent: number, total: number) => void; signal?: AbortSignal } = {},
  ): Promise<void> {
    const parts = Math.max(1, Math.ceil(file.size / partBytes))
    const sent = new Array<number>(parts).fill(0)
    const report = () =>
      opts.onProgress?.(
        Math.min(
          file.size,
          sent.reduce((a, b) => a + b, 0),
        ),
        file.size,
      )
    let next = 0
    const worker = async (): Promise<void> => {
      while (next < parts) {
        const i = next++
        const body = file.slice(i * partBytes, Math.min(file.size, (i + 1) * partBytes))
        const address = await this.partAddress(videoId, i + 1)
        await putWithProgress({
          url: address.url,
          headers: address.headers,
          body,
          ...(opts.signal ? { signal: opts.signal } : {}),
          onProgress: (f) => {
            sent[i] = f * body.size
            report()
          },
        })
        sent[i] = body.size
        report()
      }
    }
    await Promise.all(Array.from({ length: Math.min(3, parts) }, worker))
    await this.complete(videoId, { durationMs: null })
  }

  /** Keep only these parts of the original; the cut is rendered in the background. */
  async saveCuts(videoId: string, keep: VideoSegment[]): Promise<VideoSummary> {
    return (await this.http.put<{ video: VideoSummary }>(`/videos/${videoId}/cuts`, { keep })).video
  }

  /** The edit as a new video, leaving this one as it is. */
  async copyWithCuts(videoId: string, keep: VideoSegment[], title?: string): Promise<VideoSummary> {
    const body = { keep, ...(title ? { title } : {}) }
    return (await this.http.post<{ video: VideoSummary }>(`/videos/${videoId}/cuts/copy`, body))
      .video
  }

  /** Back to the original, uncut. */
  async clearCuts(videoId: string): Promise<VideoSummary> {
    return (await this.http.delete<{ video: VideoSummary }>(`/videos/${videoId}/cuts`)).video
  }

  /** Transcribe it (again). */
  async transcribe(videoId: string): Promise<VideoSummary> {
    return (await this.http.post<{ video: VideoSummary }>(`/videos/${videoId}/transcript`)).video
  }

  /** Have Claude suggest a title, summary and chapters. */
  async suggest(videoId: string): Promise<VideoSummary> {
    return (await this.http.post<{ video: VideoSummary }>(`/videos/${videoId}/insights`)).video
  }

  /** Take Claude's suggested title, chapters, or both. */
  async applySuggestion(
    videoId: string,
    what: { title?: boolean; chapters?: boolean },
  ): Promise<VideoSummary> {
    return (
      await this.http.post<{ video: VideoSummary }>(`/videos/${videoId}/insights/apply`, what)
    ).video
  }

  /** One video by its share link, as anyone with the link sees it. */
  shared(token: string): SharedVideo {
    return new SharedVideo(this.http, this.local, token)
  }

  /** A share page: its state, its polling, its editor. `open(token)` it. */
  page(): VideoPage {
    return new VideoPage(this, this.signedIn)
  }
}

/**
 * A video through its share link. A password-protected one, once unlocked,
 * gives an unlock token; it is remembered in local storage per link, so the
 * password is asked once per browser, and sent with every request here.
 */
export class SharedVideo {
  private unlockToken: string | null

  constructor(
    private readonly http: HttpClient,
    private readonly local: KeyValueStore | null,
    readonly token: string,
  ) {
    this.unlockToken = readKey(local, this.key)
  }

  private get key(): string {
    return `ls-unlock:${this.token}`
  }

  private get base(): string {
    return `/videos/shared/${this.token}`
  }

  /** The header that proves the password was given, when it was. */
  headers(): Record<string, string> {
    return this.unlockToken ? { 'x-video-unlock': this.unlockToken } : {}
  }

  private call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return this.http.request<T>(path, {
      ...init,
      headers: { ...this.headers(), ...(init.headers as Record<string, string> | undefined) },
    })
  }

  /**
   * The video and its links. A viewer the link does not admit gets an
   * ApiError: 401 `password_required` or `sign_in_required`, 410 when it
   * has expired, 403 when it is private. `polling` is the page looking
   * again, not another view.
   */
  load(polling = false): Promise<SharedVideoView> {
    return this.call<SharedVideoView>(`${this.base}${polling ? '?poll=1' : ''}`)
  }

  /** Try a password; true when it opened the video, and the token is kept. */
  async unlock(password: string): Promise<boolean> {
    const r = await this.http.post<{ unlock: string | null }>(`${this.base}/unlock`, { password })
    this.keepUnlock(r.unlock)
    return r.unlock !== null
  }

  /** Forget the unlock token: the password was changed, say. */
  forgetUnlock(): void {
    this.keepUnlock(null)
  }

  private keepUnlock(value: string | null): void {
    this.unlockToken = value
    writeKey(this.local, this.key, value)
  }

  async transcript(): Promise<VideoTranscript> {
    return (await this.call<{ transcript: VideoTranscript }>(`${this.base}/transcript`)).transcript
  }

  /** Comments and reactions. */
  feedback(): Promise<VideoFeedback> {
    return this.call<VideoFeedback>(`${this.base}/feedback`)
  }

  async react(emoji: ReactionEmoji, atMs: number): Promise<void> {
    await this.call(`${this.base}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji, atMs }),
    })
  }

  /** A comment at a moment (`atMs`), on the whole video, or a reply (`parentId`). */
  async comment(
    body: string,
    extra: { atMs?: number | null; parentId?: string } = {},
  ): Promise<void> {
    await this.call(`${this.base}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body, ...extra }),
    })
  }

  async deleteComment(commentId: string): Promise<void> {
    await this.call(`${this.base}/comments/${commentId}`, { method: 'DELETE' })
  }

  /** The WebVTT captions for a player's track element; the unlock rides in the query. */
  captionsUrl(): string {
    const k = this.unlockToken ? `?k=${encodeURIComponent(this.unlockToken)}` : ''
    return this.http.url(`${this.base}/captions.vtt${k}`)
  }

  /**
   * Begin counting a view, as this browser's anonymous viewer id (kept in
   * local storage, so a return visit is the same viewer); null when this
   * viewer is not counted (the owner, say).
   */
  async startWatch(): Promise<string | null> {
    const r = await this.call<{ watch: string | null }>(`${this.base}/watch`, {
      method: 'POST',
      body: JSON.stringify({ viewer: viewerId(this.local) }),
    })
    return r.watch
  }

  /**
   * What played since the last report, as [start, end] ms ranges on the
   * original's clock. `leaving` sends it as a request that outlives the page.
   */
  async reportWatch(
    watchId: string,
    ranges: Array<[number, number]>,
    leaving = false,
  ): Promise<void> {
    const path = `${this.base}/watch/${watchId}`
    if (leaving) {
      this.http.beacon(path, { ranges }, this.headers())
      return
    }
    await this.call(path, { method: 'POST', body: JSON.stringify({ ranges }) })
  }
}
